import React, { FC, ReactElement, ReactNode } from 'react';
import styled from 'styled-components';

import BodyText from '@ui/atoms/BodyText';
import Header from '@ui/atoms/Header';
import { SelectionButton } from '@ui/atoms/SelectableButton';

import theme from '@theme/FolxTheme';

interface IFormInputWrapperProps {
	children?: ReactNode;
	className?: string;
	error?: string;
	groupName?: string;
	header?: string;
	showError?: boolean;
	subheader?: string;
	subtext?: string;
	text?: string | ReactElement;
	variant?: 'simple' | 'small' | 'full';
}

interface IInputWrapperProps {
	simple: boolean;
}

interface ILabelContainerProps extends IInputWrapperProps {
	small: boolean;
}

const InputWrapper = styled.div<IInputWrapperProps>`
	align-items: stretch;
	display: flex;
	flex-direction: column;
	justify-content: ${({ simple }) => (simple ? 'space-between' : 'flex-start')};

	@media ${theme.responsive.breakpoints.tablet} {
		align-items: ${({ simple }) => (simple ? 'center' : 'stretch')};
		flex-direction: ${({ simple }) => (simple ? 'row' : 'column')};
	}
`;

const HeaderText = styled(Header)`
	margin-bottom: ${theme.spacing.xxl};
`;

const ErrorText = styled(BodyText)`
	margin-top: ${theme.spacing.xs};
`;

const LabelContainer = styled.div<ILabelContainerProps>`
	flex-grow: 1;
	flex-basis: ${({ simple }) => simple && '50%'};
	margin-bottom: ${({ small }) => (small ? theme.spacing.sm : theme.spacing.lg)};

	@media ${theme.responsive.breakpoints.tablet} {
		margin-right: ${({ simple }) => (simple ? theme.spacing.lg : '0')};
		margin-bottom: ${({ simple, small }) =>
			simple ? 0 : small ? theme.spacing.sm : theme.spacing.lg};
	}

	${BodyText} + ${BodyText} {
		margin-top: ${({ small }) => !small && theme.spacing.xs};
	}
`;

const ContentContainer = styled.div<IInputWrapperProps>`
	flex-grow: 1;
	flex-basis: ${({ simple }) => simple && '50%'};

	${SelectionButton} {
		width: ${({ simple }) => simple && '100%'};
	}
`;

const FormInputWrapper: FC<IFormInputWrapperProps> = ({
	children,
	className,
	error,
	groupName = '',
	header,
	showError,
	subheader,
	subtext,
	text,
	variant = 'simple',
}) => {
	const isSimpleVariant = variant === 'simple';
	const isSmallVariant = variant === 'small';

	const input = (
		<>
			<InputWrapper simple={isSimpleVariant}>
				{!!header && !isSimpleVariant && (
					<HeaderText level="5" variant="bold">
						{header}
					</HeaderText>
				)}
				{(!!subheader || !!text) && (
					<LabelContainer simple={isSimpleVariant} small={isSmallVariant}>
						{!!subheader && (
							<BodyText type="bold" size={variant === 'small' ? 'md' : 'lg'} small={isSmallVariant}>
								{subheader}
							</BodyText>
						)}
						{!!text && <BodyText size={variant === 'small' ? 'xs' : 'md'}>{text}</BodyText>}
						{!!subtext && <BodyText size="xs">{subtext}</BodyText>}
						{!!error && showError && (
							<BodyText size={variant === 'small' ? 'xs' : 'md'} color="red">
								{error}
							</BodyText>
						)}
					</LabelContainer>
				)}
				<ContentContainer simple={isSimpleVariant}>{children}</ContentContainer>
			</InputWrapper>
		</>
	);

	return (
		<div className={className}>
			{groupName ? <label htmlFor={groupName}>{input}</label> : input}
		</div>
	);
};

export default styled(FormInputWrapper)``;

/** Form Input Wrapper Types
 * simple: input with a single question or choice
 * 	- provide text only
 * small: input with a more complex question, but a simple choice
 * 	- provide subheader & text
 * full: input that requires explanation or emphasis and has a complex choice
 * 	- provide header, subheader, & text depending on designs
 */
